/**
 * L'écran de publication — **avenant A-8** (« Ce que tu as fait, ce que le public en fait »).
 *
 * Le déclencheur, dans les mots de Mathieu : *« quand on publie une vidéo on me dit 14 jours et
 * t'as eu X abonnés, sauf que notre compteur de travail il est jour numéro 2. »* L'ancien écran
 * révélait **tout l'avenir** de la vidéo d'un coup, pendant que l'horloge affichait « J2 ».
 * Après lui, il ne restait plus rien à découvrir.
 *
 * Le principe : **ce que tu as fait** — ton exécution, la classe — est connaissable tout de
 * suite ; **ce que le public en fait** arrive au fil des jours. C'est le métier même : on sait
 * quand on a fait une bonne vidéo, on ne sait jamais si elle va marcher. Et c'est la condition
 * de la persévérance posée en A-5 : on ne persévère pas dans l'incertitude si le jeu la
 * supprime.
 *
 * Trois choses valent dans les **deux** modes :
 * 1. **« Vues attendues en tout »**, jamais « Pic de vues » — le chiffre est le total de la
 *    fenêtre initiale, et l'ancien libellé était faux. C'est lui que Mathieu avait lu comme
 *    « t'as eu X abonnés ».
 * 2. **Des jours ABSOLUS** — « du J3 au J16 », jamais « sur 14 jours ». Une date absolue ne
 *    peut pas se lire comme « 14 jours viennent de passer ».
 * 3. **`Chance` et `Conversion` derrière un dépliant replié.** « Chance » annonce au joueur que
 *    son résultat vient du dé plutôt que de son travail, **juste au moment où il devrait être
 *    fier** ; « Conversion » est un rouage interne. Des sondes de debug arrivées dans un écran
 *    joueur : on les garde pour qui veut regarder sous le capot, jamais dans la face du joueur
 *    au moment du verdict.
 *
 * **Le mode ne touche jamais le moteur** : les deux produisent des résultats strictement
 * identiques. Tout est là, on choisit ce qu'on montre — donc basculer est instantané et
 * rétroactif, sans le moindre appel réseau.
 *
 * ⟦C57 · lot 8 · A-174⟧ **Moins de texte.** À l'écran : la classe, le titre de la vidéo, les chiffres qui
 * comptent (vues attendues, score), UNE ligne sur les jours, le prix d'un abonné, un bouton. Les
 * « coulisses » (la courbe, les prévisions, les rouages, ce que veut dire « rien n'est encore versé »,
 * le mot du métier) sont derrière le bouton « Les dessous » : le dépliant « ▸ les coulisses » est devenu
 * le dialogue partagé du jeu, avec le même contenu.
 */

import { RPM_LONGUE, RPM_SHORT, type GameState, type VideoRecord } from "@maxime/shared";

import { MotDeNote } from "../../atelier/core/NoteSur100";
import { noteEnMot } from "../../atelier/core/note";
import { DessousBloc, LesDessous } from "../../components/LesDessous";
import { BADGE_CLASSE, nombre } from "../../game/format";
import { marche, prixDeLaVideo } from "../../game/marche";
import { useUIStore } from "../../stores/uiStore";

import { CourbeVues } from "./CourbeVues";

export function Publication({
  video,
  sujet,
  state,
  jourDePublication,
  monetise,
  onFermer,
}: {
  video: VideoRecord;
  sujet: string;
  /** ⟦A-38⟧ L'état de la chaîne — pour dire la hauteur de la marche, pas pour calculer quoi que ce soit. */
  state: GameState;
  jourDePublication: number;
  /** ⟦A-1⟧ La couche argent n'existe qu'après le Grand Saut — sinon les revenus n'ont pas de sens. */
  monetise: boolean;
  onFermer(): void;
}) {
  const mode = useUIStore((s) => s.settings.revealMode);
  const badge = BADGE_CLASSE[video.class];
  // ⟦C62 · lot atelier · friction 7⟧ Le score d'exécution est une note sur 100, et il se lit en un mot (Raté,
  // Correct, Bien, Parfait : `atelier/core/note.ts`). Le rouge de l'annotation ne marque plus que « Raté » : un 49
  // (« Correct ») n'est plus entouré de rouge comme un échec.
  const noteExecution = Math.round(video.execution_score);
  const annotationNote = noteEnMot(noteExecution).ton === "rate" ? "entoure" : "";

  // Jours ABSOLUS : la courbe commence le lendemain de la publication (§6.2, `[0]` = J+1).
  const premierJour = jourDePublication + 1;
  const dernierJour = jourDePublication + video.remaining_curve.length;

  // ── Les prévisions des coulisses ────────────────────────────────────────────
  // Tout est DÉRIVÉ de la vidéo, rien n'est inventé : le moteur a déjà figé `vues_pic`, la
  // conversion et la courbe à la publication. On les met en forme, on ne les recalcule pas.
  const indexDuPic = video.remaining_curve.reduce(
    (meilleur, vues, i) => (vues > (video.remaining_curve[meilleur] ?? -1) ? i : meilleur),
    0,
  );
  const jourDuPic = video.remaining_curve.length > 0 ? jourDePublication + indexDuPic + 1 : null;
  const vuesDuPic = Math.round(video.remaining_curve[indexDuPic] ?? 0);
  const abonnesAttendus = Math.round(video.vues_pic * video.taux_conversion);
  const rpm = video.format === "short" ? RPM_SHORT : RPM_LONGUE;
  const revenusAttendus = (video.vues_pic / 1000) * rpm;
  // ⟦A-38⟧ La hauteur de la marche au palier courant. Deux appels au moteur, zéro règle réécrite.
  const laMarche = marche(state);

  return (
    <section className="carte scotche resultat">
      <p className="etiquette">c'est en ligne</p>
      <h2 className="resultat-titre">
        {badge.icone} {badge.libelle}
      </h2>
      <p className="resultat-sujet">« {sujet} »</p>

      {mode === "arcade" ? (
        <>
          <CourbeVues courbe={video.remaining_curve} />
          <dl className="resultat-chiffres">
            <div>
              <dt>Vues attendues en tout</dt>
              <dd className={video.class === "nulle" ? "" : "entoure"}>{nombre(video.vues_pic)}</dd>
            </div>
            <div>
              <dt>Score d'exécution</dt>
              <dd className={annotationNote}>
                {noteExecution}
                <span className="note-sur"> / 100</span>
              </dd>
              <MotDeNote note={noteExecution} />
            </div>
          </dl>
          {video.remaining_curve.length > 0 ? (
            <p className="resultat-note">
              Vues <strong>du J{premierJour} au J{dernierJour}</strong>.
            </p>
          ) : null}
        </>
      ) : (
        <>
          {/* Mode « réel » : ce que TU as fait, et rien de ce que le public en fera. */}
          <dl className="resultat-chiffres">
            <div>
              <dt>Score d'exécution</dt>
              <dd className={annotationNote}>
                {noteExecution}
                <span className="note-sur"> / 100</span>
              </dd>
              <MotDeNote note={noteExecution} />
            </div>
          </dl>
          {/* ⟦C57 · lot 8⟧ Une phrase courte ; « Voilà ce que tu as fait… », « la courbe se remplira… » et
              le mot du métier sont derrière « Les dessous ». */}
          <p className="resultat-note">
            {video.class === "nulle"
              ? "Ratée, la suivante sera meilleure."
              : `Premières vues au J${premierJour}.`}
          </p>
        </>
      )}

      {/* ⟦A-38⟧ **La marche**, et elle se dit dans les DEUX modes.
          ⟦A-8⟧ interdit de révéler ici ce que le PUBLIC fera de la vidéo — les vues. Le prix
          d'un abonné n'est pas ça : c'est l'état de la chaîne, connaissable tout de suite, au
          même titre que la classe et le score d'exécution. Et c'est le chiffre qui manquait
          depuis toujours ⟦A-35 §7.4⟧ : rien ne disait au joueur que la marche montait sous ses
          pieds. Neutre par défaut : sans vidéo publiée, `marche()` rend `null`. */}
      {laMarche ? (
        <p className="resultat-marche">
          {/* ⟦C62 · friction 7⟧ UN seul ratio, avec sa phrase : « 1 abonné = 6 vues » seul ne disait ni ce que c'est ni
              pourquoi ça compte, et les dessous en donnaient un autre (« un abonné = 5 vues »). */}
          ⛰️ <strong>{nombre(laMarche.prix)} vues</strong> pour gagner 1 abonné, et ça monte avec ta chaîne.
        </p>
      ) : null}

      {/* ⟦A-8⟧ Les coulisses — le tableau de bord qu'on ouvre quand on VEUT savoir.
          Demande de Mathieu : « c'est sympa de pouvoir voir la courbe et des trucs comme le
          YouTube Studio quand on déplie ». C'est le bon endroit : fermé, le mode « réel »
          garde son suspense ; ouvert, le joueur curieux a ses analytics — c'est lui qui
          choisit de savoir, et c'est exactement ce que fait un vrai créateur qui ouvre son
          Studio au lieu d'attendre.
          ⟦C57 · lot 8 · A-174⟧ Le dépliant « ▸ les coulisses » est devenu « Les dessous » (le dialogue
          partagé du jeu) : même contenu, plus les phrases que l'écran ne dit plus (la prévision, ce qui
          n'est pas encore versé, le mot du métier, la marche). La courbe y est dans les DEUX modes. */}
      <div className="jeu-actions">
        <button className="bouton primaire grand" type="button" onClick={onFermer}>
          Retour au studio
        </button>
        <LesDessous titre="Les coulisses de la vidéo">
          <DessousBloc titre="Prévisions de la vidéo">
            <div className="studio-analytics">
              {/* La courbe dans les DEUX modes : c'est le bouton qui la révèle, pas le mode. */}
              <CourbeVues courbe={video.remaining_curve} anime={false} />

              <dl className="studio-tuiles">
                <div>
                  <dt>Vues attendues en tout</dt>
                  <dd>{nombre(video.vues_pic)}</dd>
                </div>
                <div>
                  <dt>Meilleur jour</dt>
                  <dd>{jourDuPic === null ? "—" : `J${jourDuPic}`}</dd>
                  {jourDuPic === null ? null : <span>{nombre(vuesDuPic)} vues</span>}
                </div>
                <div>
                  <dt>Abonnés attendus</dt>
                  <dd>{nombre(abonnesAttendus)}</dd>
                  <span>{(video.taux_conversion * 100).toFixed(2).replace(".", ",")} % de conversion</span>
                  {/* ⟦A-38⟧ Le même taux, dans l'unité où il se compare : ce que CETTE vidéo a payé
                      par abonné. Il peut différer du prix du palier affiché plus haut — un short ou
                      une vidéo chaos à Fidélité < 50 porte le poids volatil, et paie donc le double
                      (§4.6). C'est exact, et c'est une propriété de la vidéo, pas du palier. */}
                  {/* ⟦C62 · friction 7⟧ Le ratio du palier est déjà dit sur l'écran : cette ligne ne reste que
                      si CETTE vidéo a payé autre chose (le poids volatil ci-dessus), dite comme telle. */}
                  {laMarche === null || Math.round(prixDeLaVideo(video)) !== Math.round(laMarche.prix) ? (
                    <span>cette vidéo : 1 abonné = {nombre(prixDeLaVideo(video))} vues</span>
                  ) : null}
                </div>
                <div>
                  <dt>Revenus attendus</dt>
                  <dd>{monetise ? `${nombre(Math.round(revenusAttendus))} ₵` : "—"}</dd>
                  {monetise ? (
                    <span>RPM {rpm.toFixed(2).replace(".", ",")} ₵/1 000</span>
                  ) : (
                    <span>pas encore monétisé</span>
                  )}
                </div>
              </dl>
            </div>
          </DessousBloc>

          {video.remaining_curve.length > 0 ? (
            <DessousBloc titre="Le versement">
              <p className="dessous-texte">
                Elles tombent <strong>du J{premierJour} au J{dernierJour}</strong>, un peu chaque
                jour. Rien n'est encore versé. Ce sont des prévisions : la chance a déjà été tirée, le
                public non.
              </p>
            </DessousBloc>
          ) : null}

          {mode === "arcade" ? null : (
            <DessousBloc titre="Ce que le public en fera">
              <p className="dessous-texte">
                {video.class === "nulle"
                  ? "Elle est mauvaise, et tu le sais : ça se sent en la finissant. La suivante sera meilleure."
                  : `Voilà ce que tu as fait. Ce que le public va en faire, tu le sauras demain : les premières vues tombent au J${premierJour}.`}
              </p>
              <p className="dessous-texte">
                La courbe se remplira jour après jour. C'est le métier : on sait
                quand on a fait une bonne vidéo, on ne sait jamais si elle va marcher.
              </p>
            </DessousBloc>
          )}

          {laMarche ? (
            <DessousBloc titre="La marche">
              <p className="dessous-texte">
                ⛰️ À ce palier, un abonné se paie <strong>{nombre(laMarche.prix)} vues</strong>.{" "}
                {Math.round(laMarche.prix) > Math.round(laMarche.prixAuRepere) ? (
                  <>
                    À {nombre(laMarche.repere)} abonnés, il t'en coûtait{" "}
                    {nombre(laMarche.prixAuRepere)}.
                  </>
                ) : null}
              </p>
            </DessousBloc>
          ) : null}

          <DessousBloc titre="Les rouages">
            <dl className="resultat-chiffres coulisses-chiffres">
              <div>
                <dt>Chance</dt>
                <dd>×{video.chance.toFixed(2).replace(".", ",")}</dd>
              </div>
              <div>
                <dt>Qualité</dt>
                <dd>{video.qualite_eff.toFixed(2).replace(".", ",")}</dd>
              </div>
              <div>
                <dt>Accroche</dt>
                <dd>{video.accroche_eff.toFixed(2).replace(".", ",")}</dd>
              </div>
              <div>
                <dt>Diffusion</dt>
                <dd>{video.diffusion_eff.toFixed(2).replace(".", ",")}</dd>
              </div>
            </dl>
          </DessousBloc>
        </LesDessous>
      </div>

    </section>
  );
}
